import { useEffect, useRef, useMemo } from 'react'
import { useWindows } from '@/hooks/useWindows'
import { deepLinkService } from '@/services/deep-link-service'
import { userManagementService } from '@/services/user-management-service'
import { authService } from '@/services/auth-service'
import { planDeepLinkSync, type DeepLinkSyncMode } from '@/lib/deep-link-sync'
import { logInfo, logError } from '@/lib/logger'
import { APPS } from '@/data/apps'

type DeepLinkWindow = Window & { __deepLinkProcessing?: boolean }

/** While set, the window manager leaves the address alone (see useWindows). */
function holdAddress(hold: boolean) {
  (window as DeepLinkWindow).__deepLinkProcessing = hold
}

/**
 * Deep links via the URL hash (`#/w=…`).
 *
 * On page load the address is applied once the window layout is final (the
 * backend state has been read, #908): a link only adds its target to the
 * desktop. An app that is already open is reused — its window is focused and
 * switched to the target — instead of opening a second one (#1188). Applying
 * earlier let the backend sync bring the old window back next to the new one.
 *
 * A hash change while the desktop runs (address bar, back/forward) makes the
 * windows match the address, with the same reuse.
 *
 * The planning lives in `planDeepLinkSync`; this hook carries it out and
 * checks app access before opening anything.
 */
export function useDeepLink() {
  const {
    openWindow,
    windows,
    layoutRestored,
    localLayout,
    closeAllWindows,
    closeWindow,
    focusWindow,
    updateWindowParams,
  } = useWindows()
  const isProcessingRef = useRef(false)
  const hasProcessedInitialRef = useRef(false)
  const lastProcessedHashRef = useRef<string>('')
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Always-current ref so async callbacks don't use stale closure values.
  // Updated synchronously during render (valid pattern — write-only during render).
  const windowsRef = useRef(windows)
  windowsRef.current = windows

  // Create stable hash of window identifiers to prevent unnecessary syncs
  // Only includes appId and params - not position, focus, subtitle, etc.
  const windowsHashRef = useRef('')
  const currentWindowsHash = useMemo(() => {
    return JSON.stringify(
      windows.map(w => ({
        id: w.id,
        appId: w.appId,
        params: w.params
      }))
    )
  }, [windows])

  useEffect(() => {
    const syncWindowsWithUrl = async (mode: DeepLinkSyncMode) => {
      if (isProcessingRef.current) {
        return
      }

      const currentHash = typeof window !== 'undefined' ? window.location.hash : ''

      // Skip if windows hash hasn't changed (prevents unnecessary syncs on focus/position changes)
      if (
        windowsHashRef.current === currentWindowsHash &&
        lastProcessedHashRef.current === currentHash
      ) {
        return
      }

      try {
        isProcessingRef.current = true
        holdAddress(true)
        windowsHashRef.current = currentWindowsHash

        const parsedWindows = deepLinkService.parseUrl()

        // Get current user from authService (Azure AD)
        const user = authService.getCachedUser()

        if (!user) {
          logError(new Error('Cannot sync windows: User not authenticated'), {
            context: 'useDeepLink'
          })
          return
        }

        // Use ref for current windows to avoid stale closure issues in async callbacks
        const currentWindows = windowsRef.current

        logInfo({
          context: 'useDeepLink',
          mode,
          urlWindowCount: parsedWindows.length,
          currentWindowCount: currentWindows.length
        }, 'Syncing windows with URL')

        if (parsedWindows.length === 0) {
          if (mode === 'reconcile' && currentWindows.length > 0) {
            logInfo({ context: 'useDeepLink' }, 'URL has no windows, closing all')
            // Machine-driven: a refusal cannot be honoured here (#850).
            closeAllWindows({ skipGuard: true })
          }
          return
        }

        const plan = planDeepLinkSync(parsedWindows, currentWindows, mode)
        if (mode === 'apply' && isOwnAddress(parsedWindows, localLayout)) {
          // Not a link: the address this tab wrote before the reload. It must
          // not bring back windows the backend layout has since dropped (#908);
          // it only brings up the minimized windows it names, as always (#516).
          plan.open = []
          plan.repoint = []
        }

        for (const windowId of plan.close) {
          logInfo({ context: 'useDeepLink', windowId }, 'Closing window the URL no longer names')
          closeWindow(windowId, { skipGuard: true })
        }

        // A link is the instruction to SHOW something: a matching window that
        // is minimized must come up, or nothing visible happens (#516).
        for (const windowId of plan.restore) {
          logInfo({ context: 'useDeepLink', windowId }, 'Restoring minimized window addressed by URL')
          focusWindow(windowId)
        }

        for (const { windowId, params } of plan.repoint) {
          logInfo({ context: 'useDeepLink', windowId }, 'Reusing open window for URL target')
          updateWindowParams(windowId, params)
          focusWindow(windowId)
        }

        for (const { appId, params } of plan.open) {
          const app = APPS.find((a) => a.id === appId)
          if (!app) {
            logError(new Error(`App not found: ${appId}`), {
              context: 'useDeepLink'
            })
            continue
          }

          // System apps are available to every user — skip the per-user
          // assignment check (e.g. admin is now system: true; the inner
          // panel handles role-based visibility).
          if (!app.system) {
            const canAccess = await userManagementService.canUserAccessApp(user.id, appId)
            if (!canAccess) {
              logError(new Error(`User does not have access to app: ${appId}`), {
                context: 'useDeepLink',
                userId: user.id,
                appId
              })
              continue
            }
          }

          // Don't pass subtitle - let the app set it based on params
          // This prevents flash of lowercase slugged subtitle
          await openWindow(appId, undefined, undefined, params)

          logInfo({
            context: 'useDeepLink',
            appId,
            hasParams: !!params,
          }, 'Opened window from URL')
        }
      } catch (error) {
        logError(error as Error, { context: 'useDeepLink.syncWindowsWithUrl', mode })
      } finally {
        lastProcessedHashRef.current = typeof window !== 'undefined' ? window.location.hash : ''
        isProcessingRef.current = false

        // Release the address once the window operations have rendered, and
        // write what is open now: a link names only its target, and a window
        // switched to it changed while the address was held.
        if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
        releaseTimerRef.current = setTimeout(() => {
          releaseTimerRef.current = null
          if (isProcessingRef.current) return
          holdAddress(false)
          deepLinkService.updateBrowserUrlFromWindows(windowsRef.current)
        }, 100)
      }
    }

    const handleHashChange = () => {
      if (hasProcessedInitialRef.current) {
        syncWindowsWithUrl('reconcile')
      }
    }

    // Handle browser back/forward navigation
    const handlePopState = () => {
      if (hasProcessedInitialRef.current) {
        syncWindowsWithUrl('reconcile')
      }
    }

    if (!hasProcessedInitialRef.current) {
      if (layoutRestored) {
        hasProcessedInitialRef.current = true
        syncWindowsWithUrl('apply')
      } else {
        // Until the layout is final, keep the window manager from replacing
        // the link in the address with the restored windows.
        holdAddress(true)
      }
    }

    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [currentWindowsHash, layoutRestored, localLayout, openWindow, closeAllWindows, closeWindow, focusWindow, updateWindowParams])

  // Leaving the desktop: a pending release would write window state into
  // whatever route comes next.
  useEffect(() => () => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
    releaseTimerRef.current = null
    holdAddress(false)
  }, [])

  return null
}

/**
 * Whether the address is exactly the layout restored from this browser's local
 * copy — what this tab wrote itself before the reload, not a link someone
 * followed. A link names only its target, so it leaves windows out or adds one.
 */
function isOwnAddress(
  parsedWindows: ReturnType<typeof deepLinkService.parseUrl>,
  localLayout: Parameters<typeof planDeepLinkSync>[1],
): boolean {
  const plan = planDeepLinkSync(parsedWindows, localLayout, 'reconcile')
  return plan.close.length === 0 && plan.open.length === 0 && plan.repoint.length === 0
}
